import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useState, type ReactNode } from 'react';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { DataTable } from './DataTable/DataTable';
import { PageHeader } from './PageHeader';
import { ConfirmDialog } from './Dialogs/ConfirmDialog';
import { FormDialog } from './Dialogs/FormDialog';
import { useCrudResource } from '../hooks/useCrudResource';
import type { CrudService } from '../../core/services/crud.service';
import type { ListQuery } from '../../core/types/api';
import { extractErrorMessage } from '../../core/services/api';

export interface CrudFormProps<T> {
  values: Partial<T>;
  setValues: (next: Partial<T>) => void;
}

interface CrudPageProps<T extends { id: number }> {
  title: string;
  description?: string;
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  /** DataGrid columns. Caller can omit the actions column — it's appended automatically. */
  columns: GridColDef[];
  renderForm: (props: CrudFormProps<T>) => ReactNode;
  /** Default values used when opening the create dialog. */
  defaultValues?: Partial<T>;
  /** Hook to mutate the create payload before sending (e.g. inject `tenantId`). */
  beforeSubmit?: (values: Partial<T>, mode: 'create' | 'edit') => Partial<T>;
  /** Optional sidebar/actions next to the page header. */
  headerActions?: ReactNode;
  /**
   * Singular form used in dialog titles ("New <entityName>", "Edit <entityName>").
   * Falls back to stripping a trailing 's' from `title`, which is wrong for
   * irregular labels like "Lines of business" → "busines"; supply this when
   * the auto-singular looks bad.
   */
  entityName?: string;
  /**
   * Extra per-row icon buttons rendered to the left of the built-in
   * edit/delete actions. Useful for "open detail" navigations and other
   * row-scoped affordances.
   */
  rowActions?: (row: T) => ReactNode;
  /** Width of the row-actions column. Bumped when `rowActions` is supplied. */
  actionsColumnWidth?: number;
  /** Extra query parameters (filter/include/sort overrides) merged into the list call. */
  listQuery?: Omit<ListQuery, 'page' | 'pageSize'>;
  /**
   * Starting mode for the View/Edit toggle. Defaults to `'edit'` so
   * existing CrudPage consumers keep their old "+ New + row icons"
   * behaviour. Pages that want the safer browsing default (matching
   * Categories / Personas / Orgs & Brands) pass `'view'`.
   *
   * Mode controls visibility of the header `+ New` button, any
   * `headerActions` block, and the per-row edit/delete icons +
   * extra `rowActions`. The toggle itself is always rendered, so the
   * user can flip between modes inline.
   */
  initialMode?: 'view' | 'edit';
}

/**
 * One-stop CRUD scaffolding for simple list-of-things pages. Handles
 * pagination, the create/edit dialog, and the delete confirmation.
 *
 * For pages that need anything more (master/detail, custom side panels,
 * server-side sorting/filtering UI) drop down to the underlying primitives.
 */
export function CrudPage<T extends { id: number }>({
  title,
  description,
  cacheKey,
  service,
  columns,
  renderForm,
  defaultValues,
  beforeSubmit,
  headerActions,
  entityName,
  rowActions,
  actionsColumnWidth,
  listQuery,
  initialMode = 'edit',
}: CrudPageProps<T>) {
  const singular = entityName ?? title.replace(/s$/, '');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  // Page-level view/edit mode. See `initialMode` doc — defaults to
  // 'edit' so we don't change the long-standing behaviour of pages
  // that haven't opted in.
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const { list, create, update, remove } = useCrudResource<T>({
    key: cacheKey,
    service,
    query: {
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      sort: '-id',
      ...listQuery,
    },
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [values, setValues] = useState<Partial<T>>(defaultValues ?? {});
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setValues(defaultValues ?? {});
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: T) => {
    setEditing(row);
    setValues(row);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const mode = editing ? 'edit' : 'create';
    const payload = beforeSubmit ? beforeSubmit(values, mode) : values;
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      setDialogOpen(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await remove.mutateAsync(confirmDelete.id);
    setConfirmDelete(null);
  };

  const fullColumns: GridColDef[] = [
    ...columns,
    {
      field: '__actions',
      headerName: '',
      sortable: false,
      filterable: false,
      // Reserve extra width per custom action so they don't squeeze the
      // built-in edit/delete pair.
      width: actionsColumnWidth ?? (rowActions ? 150 : 110),
      align: 'right',
      headerAlign: 'right',
      // `alignItems: 'center'` + `height: '100%'` so the icons sit on the
      // vertical midline of the row instead of riding along the top edge
      // (MUI X DataGrid doesn't center `renderCell` content by default).
      //
      // In view mode the cell stays empty (everything that mutates is
      // hidden); the toggle at the page header lets the user opt back
      // into editing.
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ height: '100%' }}
        >
          {mode === 'edit' && (
            <>
              {rowActions?.(params.row as T)}
              <IconButton size="small" onClick={() => openEdit(params.row as T)} aria-label="edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setConfirmDelete(params.row as T)} aria-label="delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
      ),
    },
  ];

  return (
    // Flex column so the DataTable's `flex-fill` mode can stretch into
    // the remaining viewport height instead of bottoming at a fixed pixel
    // value. Dialogs sit outside the flex flow since they portal anyway.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={title}
        description={description}
        actions={
          // Edit-only affordances first (Generate / + New / any caller
          // headerActions), then the always-visible View/Edit toggle.
          // Matches the layout used by Categories / Personas / Orgs &
          // Brands so the controls land in the same spot across the
          // app.
          <>
            {mode === 'edit' && (
              <>
                {headerActions}
                <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
                  New
                </Button>
              </>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, next) => {
                if (next === 'view' || next === 'edit') setMode(next);
              }}
              aria-label="page mode"
            >
              <ToggleButton value="view" aria-label="view mode">
                <Tooltip title="View mode">
                  <VisibilityIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="edit" aria-label="edit mode">
                <Tooltip title="Edit mode">
                  <EditNoteIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        }
      />

      <DataTable<T>
        rows={list.data?.data ?? []}
        columns={fullColumns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
      />

      <FormDialog
        open={dialogOpen}
        title={editing ? `Edit ${singular}` : `New ${singular}`}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        loading={create.isPending || update.isPending}
        submitLabel={editing ? 'Save changes' : 'Create'}
      >
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {renderForm({ values, setValues })}
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete"
        message={`Delete "${(confirmDelete as { name?: string } | null)?.name ?? `record #${confirmDelete?.id}`}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
